import React from 'react'
import { useReactiveVar } from '@apollo/client'
import { Divider } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { activeVersionVar, calculatorVar } from '../../apollo/reactive-vars'
import { Section } from '../common/Section'
import { ReportPurchasedEnergyTable } from '../system-container/report/ReportPurchasedEnergyTable'
import { ReportDemandsTable } from '../system-container/report/ReportDemandsTable'
import { Printable } from '../common/Printable'
import {
  producedEnergySubCategories,
  versionNetEnergySubCategories,
} from '../../provider/result-categories'
import { ReportProvidersProvidedTable } from '../system-container/report/ReportProvidersProvidedTable'
import { ReportProvidersPurchasedElectricityTable } from '../system-container/report/ReportProvidersPurchasedElectricityTable'
import { ReportSolarPanelsAndBatteriesTable } from '../system-container/report/ReportSolarPanelsAndBatteriesTable'
import { ResultsByBuilding } from './ResultsByBuilding'

export function ProjectReportPage(): React.ReactElement {
  const { t } = useTranslation()
  const calculator = useReactiveVar(calculatorVar)
  const activeVersion = useReactiveVar(activeVersionVar)

  if (!calculator || !activeVersion) {
    return <div />
  }

  const buildingProviders = calculator
    .getMultipliedBuildings()
    .flatMap((building) => building.getProviders())

  const providers = [
    ...buildingProviders,
    ...calculator
      .getLocalGrids()
      .flatMap((localGrid) => localGrid.getProviders()),
  ]

  const demandFlows = [
    ...calculator
      .getAllContainers()
      .flatMap((container) => container.getPrimaryDemands()),
  ]

  return (
    <Printable>
      <Section title={t('report.purchasedEnergies')}>
        <ReportPurchasedEnergyTable providers={providers} />
      </Section>

      <Divider />

      <Section title={t('report.energySystems')}>
        {/* Only show building providers to avoid duplicate energy from local grids */}
        {producedEnergySubCategories.map((subCategory) => (
          <ReportProvidersProvidedTable
            key={`provider-provided-by-${subCategory.nameKey}-table`}
            providers={buildingProviders}
            subCategory={subCategory}
          />
        ))}

        <ReportProvidersPurchasedElectricityTable providers={providers} />

        <ReportSolarPanelsAndBatteriesTable providers={providers} />
      </Section>

      <Divider />

      <Section title={t('report.totalEnergyDemands')}>
        {versionNetEnergySubCategories.map((subCategory) => (
          <ReportDemandsTable
            key={`demands-by-${subCategory.nameKey}-table`}
            demandFlows={demandFlows}
            subCategory={subCategory}
          />
        ))}
      </Section>

      <Divider />

      <Section title={t('report.resultsByBuilding')}>
        <ResultsByBuilding buildings={activeVersion.buildings} />
      </Section>
    </Printable>
  )
}
